import React, { useState, useEffect } from 'react';
import { Transaction, PendingSale, UnifiedTransaction } from '../types/payment';
import { hasPermission } from '../utils/rolePermissions';
import { useAuth } from '../context/AuthContext';
import { SettingsAPI, SalesAPI, SaleCreditTransferRecord, PaymentLinkAPI, EditHistoryEntry } from '../services/api';
import { TransactionComments } from './TransactionComments';
import { CustomerDocumentsSection } from './CustomerDocumentsSection';
import { SaleEditModal } from './SaleEditModal';
import { ProcessingSection } from './ProcessingSection';

interface TransactionDetailsPanelProps {
  transaction: UnifiedTransaction | null;
  isOpen: boolean;
  onClose: () => void;
  decryptedData?: any;
  onDecryptData?: (transactionId: string, reason: string) => void;
  onVoidRefund?: (transaction: UnifiedTransaction, action: 'void' | 'refund') => void;
  // Called after a successful detail edit so the parent can reload its data.
  onEdited?: () => void;
  // When true (processor pages only), render the company/processor-side
  // Processing section for Bill sales. The section self-gates on category + role.
  showProcessingSection?: boolean;
}

// Helper component to resolve provider names
const ProviderNames: React.FC<{ utility_providers: Record<string, string> }> = ({ utility_providers }) => {
  const [providerNames, setProviderNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchProviderNames = async () => {
      // Resolve only the provider IDs actually referenced here, by ID. The
      // bulk /settings/providers list is capped (default 100), so building the
      // map from it left any provider past the cap showing as "Unknown"; the
      // by-ID lookup also resolves deactivated providers on historical sales.
      const ids = Array.from(new Set(Object.values(utility_providers).filter(Boolean)));
      if (ids.length === 0) {
        if (active) setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const pairs = await Promise.all(
          ids.map(async (id) => {
            try {
              const provider = await SettingsAPI.getProvider(id);
              return [id, provider?.name as string | undefined] as const;
            } catch {
              return [id, undefined] as const;
            }
          })
        );
        if (!active) return;
        const providersMap: Record<string, string> = {};
        pairs.forEach(([id, name]) => {
          if (name) providersMap[id] = name;
        });
        setProviderNames(providersMap);
      } catch (error) {
        console.error('Failed to load provider names:', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProviderNames();
    return () => { active = false; };
  }, [utility_providers]);

  if (loading) {
    return <span>Loading...</span>;
  }

  return (
    <span>
      {Object.entries(utility_providers).map(([domain, providerId], index) => (
        <span key={domain}>
          {domain}: {providerNames[providerId] || `Unknown (${providerId})`}
          {index < Object.entries(utility_providers).length - 1 ? ', ' : ''}
        </span>
      ))}
    </span>
  );
};

const editSourceLabel = (s?: string | null): string =>
  s === 'processing_section' ? 'Processing' : s === 'sale_edit' ? 'Details edit' : 'Edit';
// Lists (e.g. services) join with commas; objects (e.g. service -> provider) read "Gas: Columbia Gas".
const fmtAuditVal = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map(fmtAuditVal).join(', ') : '—';
  if (typeof v === 'object') {
    const parts = Object.entries(v).map(([k, x]) => `${k}: ${fmtAuditVal(x)}`);
    return parts.length ? parts.join(', ') : '—';
  }
  return String(v);
};

export const TransactionDetailsPanel: React.FC<TransactionDetailsPanelProps> = ({
  transaction,
  isOpen,
  onClose,
  decryptedData,
  onDecryptData,
  onVoidRefund,
  onEdited,
  showProcessingSection
}) => {
  const { user } = useAuth();
  const [decryptReason, setDecryptReason] = useState('');
  const [showDecryptForm, setShowDecryptForm] = useState(false);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const canDecryptCardData = hasPermission(user?.role, 'canDecryptCardData');
  const canDecryptSSNOnly = hasPermission(user?.role, 'canDecryptSSNOnly');
  const canDecrypt = canDecryptCardData || canDecryptSSNOnly;
  const canVoidRefund = !user?.view_only && user?.role && ['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin'].includes(user.role);
  // Editing existing sale details is limited to admin / director / floor manager.
  const canEditSale = !user?.view_only && !!user?.role && ['admin', 'director', 'floor_manager'].includes(user.role);

  // Sale-credit transfer history for this transaction (who it was re-credited to + reason).
  const [transfers, setTransfers] = useState<SaleCreditTransferRecord[]>([]);
  useEffect(() => {
    if (!isOpen || !transaction) { setTransfers([]); return; }
    const txId = (transaction as any).transaction_id || transaction.id;
    if (!txId) { setTransfers([]); return; }
    let active = true;
    SalesAPI.getTransferHistory({ transaction_id: txId }).then(rows => {
      if (active) setTransfers(rows);
    });
    return () => { active = false; };
  }, [isOpen, transaction]);

  // Edit history (Activity Log) — every recorded field edit touching this record.
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  const loadEditHistory = React.useCallback(() => {
    if (!transaction) { setEditHistory([]); return; }
    const rid = (transaction as any).pending_sale_id || (transaction as any).transaction_id || transaction.id;
    if (!rid) { setEditHistory([]); return; }
    SalesAPI.getEditHistory(rid).then(setEditHistory).catch(() => setEditHistory([]));
  }, [transaction]);
  useEffect(() => {
    if (!isOpen || !transaction) { setEditHistory([]); return; }
    loadEditHistory();
  }, [isOpen, transaction, loadEditHistory]);

  // Payment-link attempt history (only when this record is a payment link —
  // links carry a `token`). Shows every charge attempt + decline reason.
  const [attempts, setAttempts] = useState<any[]>([]);
  useEffect(() => {
    const isLink = !!(transaction as any)?.token;
    if (!isOpen || !transaction || !isLink) { setAttempts([]); return; }
    let active = true;
    PaymentLinkAPI.getTransactions(transaction.id)
      .then(res => { if (active) setAttempts(res.attempts || []); })
      .catch(() => { if (active) setAttempts([]); });
    return () => { active = false; };
  }, [isOpen, transaction]);

  if (!isOpen || !transaction) return null;

  const handleDecryptSubmit = async () => {
    if (!decryptReason.trim() || !onDecryptData) return;
    
    // Determine the correct transaction ID for decryption
    // For unified transactions from pending_sales + transactions join: use transaction_id (joined field)
    // For direct transactions from transactions table: use id
    const transactionId = (transaction as any).transaction_id || transaction.id;
    
    if (!transactionId) {
      console.error('No transaction ID available for decryption. Available fields:', Object.keys(transaction));
      return;
    }
    
    try {
      setIsDecrypting(true);
      await onDecryptData(transactionId, decryptReason.trim());
      setShowDecryptForm(false);
      setDecryptReason('');
    } catch (error) {
      console.error('Decryption failed:', error);
    } finally {
      setIsDecrypting(false);
    }
  };

  const handleDecryptCancel = () => {
    setShowDecryptForm(false);
    setDecryptReason('');
  };

  const getVoidRefundEligibility = () => {
    // CRITICAL: Only allow void/refund for COMPLETED transactions from the transactions table
    // This should NOT work for pending_sales, rejected transactions, or preauth-only transactions
    
    // 1. Must be a completed transaction (sale or capture, not void/refund/preauth)
    // Captured transactions can have tx_type of 'sale' or 'capture'
    const txType = (transaction as Transaction).tx_type;
    if (txType === 'void' || txType === 'refund') {
      return { canVoid: false, canRefund: false, reason: 'This is already a void/refund transaction' };
    }
    
    if (txType === 'preauth' || txType === 'auth') {
      return { canVoid: false, canRefund: false, reason: 'Only captured transactions can be voided/refunded' };
    }
    
    // 2. Must have been successful (response code 100)
    const responseCode = (transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as PendingSale).preauth_response_code;
    if (responseCode && responseCode !== '100') {
      return { canVoid: false, canRefund: false, reason: 'Only successful transactions can be voided/refunded' };
    }
    
    // 3. Must not already be voided or refunded
    if ((transaction as any).is_voided === true) {
      return { canVoid: false, canRefund: false, reason: 'Transaction has already been voided' };
    }
    
    if ((transaction as any).is_refunded === true) {
      return { canVoid: false, canRefund: false, reason: 'Transaction has already been refunded' };
    }
    
    // 4. Must be from the transactions table (completed transactions), not pending_sales
    // We can check this by ensuring we have an NMI transaction ID (completed transactions have this)
    const transactionId = (transaction as any).nmi_transactionid || (transaction as any).nmi_transaction_id;
    if (!transactionId) {
      return { canVoid: false, canRefund: false, reason: 'Only completed transactions can be voided/refunded' };
    }
    
    // 5. NMI settlement status (kept current by the backend's 15-minute sync):
    // not settled yet = void, settled = refund.
    const settlement = (transaction as any).settlement_status;
    if (settlement === 'pending') {
      return { canVoid: true, canRefund: false, reason: 'Not settled yet - can be voided' };
    }
    if (settlement === 'settled') {
      return { canVoid: false, canRefund: true, reason: 'Settled transaction - can be refunded' };
    }
    if (settlement === 'voided') {
      return { canVoid: false, canRefund: false, reason: 'Transaction has been voided in NMI' };
    }

    // 6. Not checked against NMI yet: fall back to the date (same day = void)
    const transactionDate = new Date(transaction.created_at).toDateString();
    const today = new Date().toDateString();
    
    if (transactionDate === today) {
      return { canVoid: true, canRefund: false, reason: 'Same-day transaction - can be voided' };
    } else {
      return { canVoid: false, canRefund: true, reason: 'Settled transaction - can be refunded' };
    }
  };

  const eligibility = getVoidRefundEligibility();

  const formatCardNumber = (encrypted?: string, last4?: string, masked?: string) => {
    if (canDecryptCardData && decryptedData?.decrypted_card_data?.card_number) {
      return decryptedData.decrypted_card_data.card_number;
    }
    if (masked) {
      return masked;
    }
    return last4 ? `****-****-****-${last4}` : '****-****-****-****';
  };

  const formatExpiry = (encrypted?: string, expMonth?: string, expYear?: string) => {
    if (canDecryptCardData && decryptedData?.decrypted_card_data?.exp_month && decryptedData?.decrypted_card_data?.exp_year) {
      return `${decryptedData.decrypted_card_data.exp_month}/${decryptedData.decrypted_card_data.exp_year}`;
    }
    if (expMonth && expYear) {
      return `${expMonth}/${expYear}`;
    }
    return '**/**';
  };

  const formatCVV = (encrypted?: string) => {
    if (canDecryptCardData && decryptedData?.decrypted_card_data?.cvv) {
      return decryptedData.decrypted_card_data.cvv;
    }
    return '***';
  };

  const formatSSN = (encrypted?: string, last4?: string) => {
    if (canDecrypt && decryptedData?.decrypted_card_data?.ssn) {
      // Format as XXX-XX-XXXX
      const ssn = decryptedData.decrypted_card_data.ssn;
      if (ssn && ssn.length === 9) {
        return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
      }
      return decryptedData.decrypted_card_data.ssn;
    }
    return last4 ? `***-**-${last4}` : '***-**-****';
  };

  const formatDL = (last4?: string) => {
    // Show the full DL number only when it has been decrypted for an authorized user
    if (canDecrypt && decryptedData?.decrypted_card_data?.drivers_license_number) {
      return decryptedData.decrypted_card_data.drivers_license_number;
    }
    return last4 ? `•••• ${last4}` : '••••';
  };

  const getStatusColor = (status?: string, responseCode?: string) => {
    const code = responseCode || (transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as PendingSale).preauth_response_code;
    if (code === '100') return 'success';
    if (code && code !== '100') return 'error';
    if (status?.toLowerCase().includes('success') || status?.toLowerCase().includes('approved')) return 'success';
    if (status?.toLowerCase().includes('failed') || status?.toLowerCase().includes('declined')) return 'error';
    return 'warning';
  };

  const formatTxType = (txType: string) => {
    if (!txType) return 'Pending Sale';
    
    const typeMap: { [key: string]: string } = {
      'sale': 'Sale',
      'auth': 'Authorization',
      'validate': 'Validation',
      'capture': 'Capture',
      'void': 'Void',
      'refund': 'Refund'
    };
    return typeMap[txType] || txType.charAt(0).toUpperCase() + txType.slice(1);
  };

  return (
    <div className="details-panel-overlay" onClick={onClose}>
      <div className="details-panel" onClick={e => e.stopPropagation()}>
        <div className="details-header">
          <h2>Transaction Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {canEditSale && (
              <button className="btn btn-outline-primary btn-sm" onClick={() => setShowEdit(true)}>
                ✎ Edit
              </button>
            )}
            <button className="btn btn-ghost btn-sm close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {showEdit && transaction && (
          <SaleEditModal
            transaction={transaction}
            onClose={() => setShowEdit(false)}
            onSaved={() => { onEdited?.(); }}
          />
        )}

        <div className="details-content">
          {/* Basic Transaction Info */}
          <div className="details-section">
            <h3>Transaction Overview</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Transaction ID:</label>
                <span className="monospace">{transaction.id}</span>
              </div>
              <div className="detail-item">
                <label>Type:</label>
                <span className="tx-type-badge">{formatTxType((transaction as any).tx_type)}</span>
              </div>
              {transaction.amount !== undefined && transaction.amount !== null && (
                <div className="detail-item">
                  <label>Amount:</label>
                  <span className="amount">${Number(transaction.amount).toFixed(2)} {transaction.currency || 'USD'}</span>
                </div>
              )}
              {transaction.status && (
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusColor(transaction.status, (transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as PendingSale).preauth_response_code)}`}>
                    {transaction.status}
                  </span>
                </div>
              )}
              {transaction.category && (
                <div className="detail-item">
                  <label>Sale Type:</label>
                  <span className="sale-category">{transaction.category}</span>
                </div>
              )}
              {transaction.sale_state && (
                <div className="detail-item">
                  <label>Sale State:</label>
                  <span className="sale-state">{transaction.sale_state}</span>
                </div>
              )}
              {transaction.tfn && (
                <div className="detail-item">
                  <label>TFN:</label>
                  <span className="sale-tfn">{transaction.tfn}</span>
                </div>
              )}
              {transaction.sale_domains && transaction.sale_domains.length > 0 && (
                <div className="detail-item">
                  <label>Sale Domains:</label>
                  <span className="sale-domains">{transaction.sale_domains.join(', ')}</span>
                </div>
              )}
              {transaction.utility_providers && Object.keys(transaction.utility_providers).length > 0 && (
                <div className="detail-item">
                  <label>Utility Providers:</label>
                  <span className="utility-providers">
                    <ProviderNames utility_providers={transaction.utility_providers} />
                  </span>
                </div>
              )}
              {transaction.sale_notes && (
                <div className="detail-item">
                  <label>Sale Notes:</label>
                  <span className="sale-notes">{transaction.sale_notes}</span>
                </div>
              )}
            </div>
            {((transaction as any).nmi_response_text || (transaction as any).response_text) && (
              <div className="detail-item full-width">
                <label>Description:</label>
                <span>{(transaction as any).nmi_response_text || (transaction as any).response_text}</span>
              </div>
            )}

            {/* Void/Refund Actions - Only show for completed transactions with proper callback */}
            {canVoidRefund && onVoidRefund && (eligibility.canVoid || eligibility.canRefund) && (
              <div className="void-refund-actions">
                <div className="void-refund-info">
                  <strong>Transaction Actions:</strong>
                  <small>This transaction is eligible for {eligibility.canVoid ? 'voiding (not settled yet)' : 'refunding (settled)'}</small>
                </div>
                {eligibility.canVoid && (
                  <button
                    className="btn btn-warning btn-sm"
                    onClick={() => onVoidRefund(transaction, 'void')}
                    title="Void this transaction - releases funds immediately"
                  >
                    ❌ Void Transaction
                  </button>
                )}
                {eligibility.canRefund && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onVoidRefund(transaction, 'refund')}
                    title="Refund this transaction - takes 3-7 business days"
                  >
                    💰 Refund Transaction
                  </button>
                )}
              </div>
            )}
            
            {/* Debug info for ineligible transactions (only in development) */}
            {canVoidRefund && onVoidRefund && !eligibility.canVoid && !eligibility.canRefund && (
              <div className="void-refund-debug" style={{ display: process.env.NODE_ENV === 'development' ? 'block' : 'none' }}>
                <small style={{ color: 'var(--text-secondary)' }}>
                  Void/Refund not available: {eligibility.reason}
                </small>
              </div>
            )}
          </div>

          {/* Approval/Rejection Information */}
          {(transaction.approved_by || transaction.rejected_by || transaction.approved_by_name || transaction.rejected_by_name ||
            (transaction as any).voided_by || (transaction as any).refunded_by || (transaction as any).tx_type === 'void' || (transaction as any).tx_type === 'refund' ||
            transfers.length > 0) && (
            <div className="details-section">
              <h3>Transaction Workflow</h3>
              <div className="workflow-timeline">
                
                {/* Original Approval Information */}
                {(transaction.approved_by || transaction.approved_by_name) && (
                  <div className="workflow-step approval-step">
                    <div className="step-icon">✅</div>
                    <div className="step-content">
                      <div className="step-title">Transaction Approved</div>
                      <div className="step-details">
                        <span className="step-user">By: {transaction.approved_by_name || transaction.approved_by}</span>
                        {transaction.approved_at && (
                          <span className="step-date">On: {new Date(transaction.approved_at).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Rejection Information */}
                {(transaction.rejected_by || transaction.rejected_by_name) && (
                  <div className="workflow-step rejection-step">
                    <div className="step-icon">❌</div>
                    <div className="step-content">
                      <div className="step-title">Transaction Rejected</div>
                      <div className="step-details">
                        <span className="step-user">By: {transaction.rejected_by_name || transaction.rejected_by}</span>
                        {transaction.rejected_at && (
                          <span className="step-date">On: {new Date(transaction.rejected_at).toLocaleString()}</span>
                        )}
                        {transaction.rejection_reason && (
                          <div className="step-reason">
                            <strong>Reason:</strong> {transaction.rejection_reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Void Information */}
                {((transaction as any).voided_by || (transaction as any).tx_type === 'void') && (
                  <div className="workflow-step void-step">
                    <div className="step-icon">🚫</div>
                    <div className="step-content">
                      <div className="step-title">
                        {(transaction as any).tx_type === 'void' ? 'This is a Void Transaction' : 'Transaction Voided'}
                      </div>
                      <div className="step-details">
                        {((transaction as any).voided_by || (transaction as any).voided_by_name) && (
                          <span className="step-user">By: {(transaction as any).voided_by_name || (transaction as any).voided_by}</span>
                        )}
                        {(transaction as any).voided_at && (
                          <span className="step-date">On: {new Date((transaction as any).voided_at).toLocaleString()}</span>
                        )}
                        {(transaction as any).void_refund_reason && (
                          <div className="step-reason">
                            <strong>Reason:</strong> {(transaction as any).void_refund_reason}
                          </div>
                        )}
                        {(transaction as any).tx_type === 'void' && (
                          <div className="step-note">
                            💡 This transaction voids a previously approved transaction
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Refund Information */}
                {((transaction as any).refunded_by || (transaction as any).tx_type === 'refund') && (
                  <div className="workflow-step refund-step">
                    <div className="step-icon">💰</div>
                    <div className="step-content">
                      <div className="step-title">
                        {(transaction as any).tx_type === 'refund' ? 'This is a Refund Transaction' : 'Transaction Refunded'}
                      </div>
                      <div className="step-details">
                        {((transaction as any).refunded_by || (transaction as any).refunded_by_name) && (
                          <span className="step-user">By: {(transaction as any).refunded_by_name || (transaction as any).refunded_by}</span>
                        )}
                        {(transaction as any).refunded_at && (
                          <span className="step-date">On: {new Date((transaction as any).refunded_at).toLocaleString()}</span>
                        )}
                        {(transaction as any).void_refund_reason && (
                          <div className="step-reason">
                            <strong>Reason:</strong> {(transaction as any).void_refund_reason}
                          </div>
                        )}
                        {(transaction as any).tx_type === 'refund' && (
                          <div className="step-note">
                            💡 This transaction refunds a previously approved transaction
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Sale Credit Transfer(s) — who the sale was re-credited to and why */}
                {transfers.map((tr) => (
                  <div className="workflow-step transfer-step" key={tr.id}>
                    <div className="step-icon">🔁</div>
                    <div className="step-content">
                      <div className="step-title">Sale Credit Transferred</div>
                      <div className="step-details">
                        <span className="step-user">
                          To: {tr.to_user_name || tr.to_user_id}
                          {tr.from_user_name ? ` (from ${tr.from_user_name})` : ''}
                        </span>
                        {(tr.transferred_by_name || tr.transferred_by) && (
                          <span className="step-user">By: {tr.transferred_by_name || tr.transferred_by}</span>
                        )}
                        {tr.transferred_at && (
                          <span className="step-date">On: {new Date(tr.transferred_at).toLocaleString()}</span>
                        )}
                        {tr.reason && (
                          <div className="step-reason">
                            <strong>Reason:</strong> {tr.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transaction Metadata */}
          <div className="details-section">
            <h3>Transaction Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Created By:</label>
                <span>{transaction.created_by_name || transaction.created_by || 'Unknown'}</span>
              </div>
              <div className="detail-item">
                <label>Created At:</label>
                <span>{new Date(transaction.created_at).toLocaleString()}</span>
              </div>
              {transaction.updated_at && (
                <div className="detail-item">
                  <label>Updated At:</label>
                  <span>{new Date(transaction.updated_at).toLocaleString()}</span>
                </div>
              )}
              {((transaction as any).processor_name || (transaction as any).processor_id) && (
                <div className="detail-item">
                  <label>Processor:</label>
                  <span>{(transaction as any).processor_name || (transaction as any).processor_id}</span>
                </div>
              )}
              {/* Processing Assignment Information */}
              {(transaction as any).processing_status && (
                <div className="detail-item">
                  <label>Processing Status:</label>
                  <span className={`processing-status ${(transaction as any).processing_status?.toLowerCase()}`}>
                    {(transaction as any).processing_status?.replace('_', ' ')}
                  </span>
                </div>
              )}
              {(transaction as any).assigned_to_processor_name && (
                <div className="detail-item">
                  <label>Assigned to Processor:</label>
                  <span className="processor-name">{(transaction as any).assigned_to_processor_name}</span>
                </div>
              )}
              {(transaction as any).assigned_by_name && (
                <div className="detail-item">
                  <label>Assigned By:</label>
                  <span className="assigned-by-name">{(transaction as any).assigned_by_name}</span>
                </div>
              )}
              {(transaction as any).assigned_at && (
                <div className="detail-item">
                  <label>Assigned At:</label>
                  <span>{new Date((transaction as any).assigned_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* NMI Response Information */}
          {((transaction as any).nmi_transactionid || (transaction as any).nmi_transaction_id || (transaction as any).preauth_transactionid || (transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as any).preauth_response_code || (transaction as any).nmi_authcode || (transaction as any).preauth_authcode) && (
            <div className="details-section">
              <h3>NMI Response</h3>
              <div className="details-grid">
                {((transaction as any).nmi_transactionid || (transaction as any).nmi_transaction_id || (transaction as any).preauth_transactionid) && (
                  <div className="detail-item">
                    <label>NMI Transaction ID:</label>
                    <span className="monospace">{(transaction as any).nmi_transactionid || (transaction as any).nmi_transaction_id || (transaction as any).preauth_transactionid}</span>
                  </div>
                )}
                {((transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as any).preauth_response_code) && (
                  <div className="detail-item">
                    <label>Response Code:</label>
                    <span className={`response-code ${((transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as any).preauth_response_code) === '100' ? 'success' : 'error'}`}>
                      {(transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as any).preauth_response_code}
                      {((transaction as any).nmi_response_code || (transaction as any).response_code || (transaction as any).preauth_response_code) === '100' && ' (Success)'}
                    </span>
                  </div>
                )}
                {((transaction as any).nmi_response_text || (transaction as any).response_text || (transaction as any).preauth_response_text) && (
                  <div className="detail-item full-width">
                    <label>Response Text:</label>
                    <span>{(transaction as any).nmi_response_text || (transaction as any).response_text || (transaction as any).preauth_response_text}</span>
                  </div>
                )}
                {((transaction as any).nmi_authcode || (transaction as any).preauth_authcode) && (
                  <div className="detail-item">
                    <label>Auth Code:</label>
                    <span className="monospace">{(transaction as any).nmi_authcode || (transaction as any).preauth_authcode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Order Information */}
          {(transaction.orderid || transaction.ponumber || transaction.order_description || transaction.reference_id) && (
            <div className="details-section">
              <h3>Order Information</h3>
              <div className="details-grid">
                {transaction.orderid && (
                  <div className="detail-item">
                    <label>Order ID:</label>
                    <span className="monospace">{transaction.orderid}</span>
                  </div>
                )}
                {transaction.ponumber && (
                  <div className="detail-item">
                    <label>PO Number:</label>
                    <span>{transaction.ponumber}</span>
                  </div>
                )}
                {transaction.reference_id && (
                  <div className="detail-item">
                    <label>Reference ID:</label>
                    <span className="monospace">{transaction.reference_id}</span>
                  </div>
                )}
                {transaction.order_description && (
                  <div className="detail-item full-width">
                    <label>Order Description:</label>
                    <span>{transaction.order_description}</span>
                  </div>
                )}
                {transaction.notes && (
                  <div className="detail-item full-width">
                    <label>Notes:</label>
                    <span>{transaction.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Customer Information */}
          {(transaction.first_name || transaction.last_name || transaction.company || transaction.email || transaction.phone || transaction.last4_ssn || decryptedData?.decrypted_card_data?.ssn) && (
            <div className="details-section">
              <h3>Customer Information</h3>
              <div className="details-grid">
                {(transaction.first_name || transaction.last_name) && (
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{transaction.first_name} {transaction.last_name}</span>
                  </div>
                )}
                {transaction.company && (
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{transaction.company}</span>
                  </div>
                )}
                {transaction.email && (
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{transaction.email}</span>
                  </div>
                )}
                {transaction.phone && (
                  <div className="detail-item">
                    <label>Phone:</label>
                    <span>{transaction.phone}</span>
                  </div>
                )}
                {transaction.website && (
                  <div className="detail-item">
                    <label>Website:</label>
                    <span>{transaction.website}</span>
                  </div>
                )}
                {transaction.fax && (
                  <div className="detail-item">
                    <label>Fax:</label>
                    <span>{transaction.fax}</span>
                  </div>
                )}
                {/* Show the SSN row when we have either the masked last-4 OR a freshly
                    decrypted SSN. Gating on last4_ssn alone hid the row entirely for rows
                    that have an encrypted SSN but no stored last-4, so even a successful
                    decryption could never surface the value. */}
                {(transaction.last4_ssn || decryptedData?.decrypted_card_data?.ssn) && (
                  <div className="detail-item">
                    <label>SSN:</label>
                    <span className="monospace">{formatSSN(transaction.encrypted_ssn, transaction.last4_ssn)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Billing Address */}
          {(transaction.address1 || transaction.address || transaction.city || transaction.state || transaction.zip) && (
            <div className="details-section">
              <h3>Billing Address</h3>
              <div className="details-grid">
                {(transaction.address1 || transaction.address) && (
                  <div className="detail-item full-width">
                    <label>Address:</label>
                    <span>
                      {transaction.address1 || transaction.address}
                      {(transaction.address2 || transaction.address_cont) && <><br/>{transaction.address2 || transaction.address_cont}</>}
                    </span>
                  </div>
                )}
                {transaction.city && (
                  <div className="detail-item">
                    <label>City:</label>
                    <span>{transaction.city}</span>
                  </div>
                )}
                {transaction.state && (
                  <div className="detail-item">
                    <label>State:</label>
                    <span>{transaction.state}</span>
                  </div>
                )}
                {transaction.zip && (
                  <div className="detail-item">
                    <label>ZIP:</label>
                    <span>{transaction.zip}</span>
                  </div>
                )}
                {transaction.country && (
                  <div className="detail-item">
                    <label>Country:</label>
                    <span>{transaction.country}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Service Address (stored in shipping_* columns; not sent to NMI) */}
          {(transaction.shipping_address_1 || transaction.shipping_city || transaction.shipping_state || transaction.shipping_zip || transaction.shipping_firstname) && (
            <div className="details-section">
              <h3>Service Address</h3>
              <div className="details-grid">
                {(transaction.shipping_firstname || transaction.shipping_lastname) && (
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{`${transaction.shipping_firstname || ''} ${transaction.shipping_lastname || ''}`.trim()}</span>
                  </div>
                )}
                {transaction.shipping_company && (
                  <div className="detail-item">
                    <label>Company:</label>
                    <span>{transaction.shipping_company}</span>
                  </div>
                )}
                {transaction.shipping_address_1 && (
                  <div className="detail-item full-width">
                    <label>Address:</label>
                    <span>
                      {transaction.shipping_address_1}
                      {transaction.shipping_address_2 && <><br/>{transaction.shipping_address_2}</>}
                    </span>
                  </div>
                )}
                {transaction.shipping_city && (
                  <div className="detail-item">
                    <label>City:</label>
                    <span>{transaction.shipping_city}</span>
                  </div>
                )}
                {transaction.shipping_state && (
                  <div className="detail-item">
                    <label>State:</label>
                    <span>{transaction.shipping_state}</span>
                  </div>
                )}
                {transaction.shipping_zip && (
                  <div className="detail-item">
                    <label>ZIP:</label>
                    <span>{transaction.shipping_zip}</span>
                  </div>
                )}
                {transaction.shipping_country && (
                  <div className="detail-item">
                    <label>Country:</label>
                    <span>{transaction.shipping_country}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Information */}
          {(transaction.last4 || transaction.card_brand || transaction.masked_pan || canDecrypt) && (
            <div className="details-section">
              <h3>Payment Information</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Card Number:</label>
                  <span className="monospace">{formatCardNumber(transaction.encrypted_pan, transaction.last4, transaction.masked_pan)}</span>
                </div>
                {transaction.card_brand && (
                  <div className="detail-item">
                    <label>Card Brand:</label>
                    <span className="card-brand">{transaction.card_brand.toUpperCase()}</span>
                  </div>
                )}
                {canDecryptCardData && (
                  <>
                    <div className="detail-item">
                      <label>Expiry:</label>
                      <span className="monospace">{formatExpiry(transaction.encrypted_exp, transaction.exp_month, transaction.exp_year)}</span>
                    </div>
                    <div className="detail-item">
                      <label>CVV:</label>
                      <span className="monospace">{formatCVV(transaction.encrypted_cvv)}</span>
                    </div>
                  </>
                )}
              </div>
              {canDecrypt && decryptedData && (
                <div className="decrypt-warning">
                  ⚠️ Decrypted {canDecryptCardData ? 'payment data and SSN' : 'SSN'} {canDecryptCardData ? 'are' : 'is'} visible. This action has been logged for security purposes.
                </div>
              )}
            </div>
          )}

          {/* Financial Information */}
          {(transaction.shipping_amount || transaction.sales_tax || transaction.tax_exempt) && (
            <div className="details-section">
              <h3>Financial Details</h3>
              <div className="details-grid">
                {transaction.shipping_amount && (
                  <div className="detail-item">
                    <label>Shipping Amount:</label>
                    <span>${Number(transaction.shipping_amount).toFixed(2)}</span>
                  </div>
                )}
                {transaction.sales_tax && (
                  <div className="detail-item">
                    <label>Sales Tax:</label>
                    <span>${Number(transaction.sales_tax).toFixed(2)}</span>
                  </div>
                )}
                {transaction.tax_exempt && (
                  <div className="detail-item">
                    <label>Tax Exempt:</label>
                    <span>Yes</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Information */}
          {(transaction.date_of_birth || transaction.account_number || transaction.dl_last4 || decryptedData?.decrypted_card_data?.drivers_license_number || transaction.drivers_license_state || transaction.dl_issuing_date || transaction.dl_expiration_date || transaction.delivery_method) && (
            <div className="details-section">
              <h3>Additional Information</h3>
              <div className="details-grid">
                {transaction.date_of_birth && (
                  <div className="detail-item">
                    <label>Date of Birth:</label>
                    <span>{transaction.date_of_birth}</span>
                  </div>
                )}
                {transaction.delivery_method && (
                  <div className="detail-item">
                    <label>Preferred Send Method:</label>
                    <span>{transaction.delivery_method === 'sms' ? '💬 SMS' : transaction.delivery_method === 'both' ? '✉️💬 Both' : '✉️ Email'}</span>
                  </div>
                )}
                {transaction.account_number && (
                  <div className="detail-item">
                    <label>Account Number:</label>
                    <span className="monospace">{transaction.account_number}</span>
                  </div>
                )}
                {(transaction.dl_last4 || decryptedData?.decrypted_card_data?.drivers_license_number) && (
                  <div className="detail-item">
                    <label>Driver's License #:</label>
                    <span className="monospace">{formatDL(transaction.dl_last4)}</span>
                  </div>
                )}
                {transaction.drivers_license_state && (
                  <div className="detail-item">
                    <label>Issuing State:</label>
                    <span>{transaction.drivers_license_state}</span>
                  </div>
                )}
                {transaction.dl_issuing_date && (
                  <div className="detail-item">
                    <label>DLN Issuing Date:</label>
                    <span>{transaction.dl_issuing_date}</span>
                  </div>
                )}
                {transaction.dl_expiration_date && (
                  <div className="detail-item">
                    <label>DLN Expiration Date:</label>
                    <span>{transaction.dl_expiration_date}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Processing section (Bill sales, processor pages). Self-gates on
              category === 'Bill' and processing-team role. Rendered above the
              decrypt section; sensitive values reveal via the decrypt flow. */}
          {showProcessingSection && (
            <ProcessingSection
              saleId={(transaction as any).pending_sale_id || (transaction as any).transaction_id || transaction.id}
              category={transaction.category}
              decryptedData={decryptedData}
              onSaved={() => { onEdited?.(); loadEditHistory(); }}
            />
          )}

          {/* Card Data Decryption */}
          {canDecrypt && onDecryptData && (
            <div className="details-section">
              <h3>🔒 Decrypt Sensitive Data</h3>
              {!decryptedData && !showDecryptForm && (
                <div className="decrypt-intro">
                  <p>Click the button below to decrypt card data and SSN. You will need to provide a reason for this action.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowDecryptForm(true)}
                  >
                    🔓 Request Decryption
                  </button>
                </div>
              )}
              
              {showDecryptForm && !decryptedData && (
                <div className="decrypt-form">
                  <div className="form-group">
                    <label htmlFor="decrypt-reason">Reason for Decryption (Required)*</label>
                    <textarea
                      id="decrypt-reason"
                      value={decryptReason}
                      onChange={(e) => setDecryptReason(e.target.value)}
                      placeholder="Please provide a detailed reason for why you need to decrypt this sensitive data..."
                      rows={3}
                      disabled={isDecrypting}
                    />
                  </div>
                  <div className="decrypt-actions">
                    <button 
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleDecryptCancel}
                      disabled={isDecrypting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      className="btn btn-primary"
                      onClick={handleDecryptSubmit}
                      disabled={!decryptReason.trim() || isDecrypting}
                    >
                      {isDecrypting ? '🔄 Decrypting...' : '🔓 Decrypt Data'}
                    </button>
                  </div>
                </div>
              )}
              
              {decryptedData && (
                <div className="decrypt-success">
                  <div className="decrypt-warning">
                    ⚠️ <strong>Sensitive data has been decrypted and is now visible above.</strong><br/>
                    This action has been logged for security and audit purposes.
                  </div>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      setShowDecryptForm(false);
                      setDecryptReason('');
                    }}
                  >
                    ✓ Acknowledged
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Payment-link attempt history (links only) */}
          {attempts.length > 0 && (
            <div className="details-section">
              <h3>Payment Attempts ({attempts.length})</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--text-secondary, #6b7280)' }}>
                      <th style={{ padding: '0.4rem' }}>When</th>
                      <th style={{ padding: '0.4rem' }}>Type</th>
                      <th style={{ padding: '0.4rem' }}>Amount</th>
                      <th style={{ padding: '0.4rem' }}>Result</th>
                      <th style={{ padding: '0.4rem' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map((a: any) => (
                      <tr key={a.id} style={{ borderTop: '1px solid var(--border-primary, #e5e7eb)' }}>
                        <td style={{ padding: '0.4rem', whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleString()}</td>
                        <td style={{ padding: '0.4rem' }}>{a.tx_type}</td>
                        <td style={{ padding: '0.4rem' }}>${Number(a.amount).toFixed(2)}</td>
                        <td style={{ padding: '0.4rem', fontWeight: 600, color: a.status === 'approved' ? '#059669' : '#dc2626' }}>
                          {a.status === 'approved' ? '✓ Approved' : '✗ Failed'}
                        </td>
                        <td style={{ padding: '0.4rem' }}>{a.status === 'approved' ? '—' : (a.reason || `Code ${a.response_code || '?'}`)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Activity Log — recorded field edits touching this record (all panels). */}
          <div className="details-section">
            <h3>Activity Log</h3>
            {editHistory.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No edits recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {editHistory.map((e) => (
                  <div key={e.id} style={{ borderLeft: '3px solid var(--primary-light)', paddingLeft: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>{e.edited_by_name || e.edited_by || 'Unknown'}</strong>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, background: 'var(--primary-light)', color: 'var(--primary)', padding: '1px 8px', borderRadius: '999px' }}>{editSourceLabel(e.source)}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{e.edited_at}</span>
                    </div>
                    <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                      {Object.entries(e.changes || {}).map(([field, ch]) => (
                        <li key={field} style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{field}</span>: {fmtAuditVal(ch?.old)} → {fmtAuditVal(ch?.new)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer document-upload link + documents */}
          <div className="details-section">
            <CustomerDocumentsSection
              recordId={(transaction as any).pending_sale_id || (transaction as any).transaction_id || transaction.id}
              recordTitle={`${transaction.first_name || ''} ${transaction.last_name || ''}`.trim() || 'Customer'}
            />
          </div>

          {/* Comments Section */}
          <div className="details-section">
            <TransactionComments recordId={transaction.id} />
          </div>
        </div>
      </div>

      <style>{`
        .details-panel-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .details-panel {
          background: var(--bg-card);
          border-radius: 12px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid var(--border-primary);
          background: var(--bg-secondary);
        }

        .details-header h2 {
          margin: 0;
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 600;
        }

        .close-btn {
          font-size: 1.5rem;
          width: 2.5rem;
          height: 2.5rem;
        }

        .details-content {
          padding: 1.5rem;
          max-height: calc(90vh - 100px);
          overflow-y: auto;
          background: var(--bg-secondary);
        }

        .details-section {
          margin-bottom: 2.5rem;
          padding: 1.5rem;
          background: var(--bg-card);
          border-radius: 8px;
          border: 1px solid var(--border-primary);
        }

        .details-section:last-child {
          margin-bottom: 0;
        }

        .details-section h3 {
          margin: 0 0 1.25rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid var(--primary-light);
        }

        .details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem 2rem;
          align-items: start;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          align-items: flex-start;
        }

        .detail-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-item label {
          font-weight: 500;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .detail-item span {
          color: var(--text-primary);
          font-size: 0.9375rem;
        }

        .monospace {
          font-family: 'Courier New', monospace;
          font-weight: 500;
        }

        .amount {
          font-weight: 600;
          color: var(--success);
          font-size: 1rem;
        }

        .status-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-badge.success {
          background: var(--success-light);
          color: var(--success);
        }

        .status-badge.error {
          background: var(--danger-light);
          color: var(--danger);
        }

        .status-badge.warning {
          background: var(--warning-light);
          color: var(--warning);
        }


        .tx-type-badge {
          display: inline-flex;
          align-items: center;
          padding: 0 0.5rem;
          background: var(--primary-light);
          color: var(--primary);
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 500;
          width: fit-content;
          height: 24px;
          line-height: 1;
          vertical-align: middle;
        }

        .response-code.success {
          color: var(--success);
          font-weight: 600;
        }

        .response-code.error {
          color: var(--danger);
          font-weight: 600;
        }

        .card-brand {
          font-weight: 600;
          color: var(--primary);
        }

        .processing-status {
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .processing-status.pending_assignment {
          background: var(--warning-light);
          color: var(--warning);
        }

        .processing-status.assigned {
          background: var(--primary-light);
          color: var(--primary);
        }

        .processing-status.in_process {
          background: var(--info-light);
          color: var(--info);
        }

        .processing-status.completed {
          background: var(--success-light);
          color: var(--success);
        }

        .processing-status.cancelled {
          background: var(--danger-light);
          color: var(--danger);
        }

        .processor-name {
          font-weight: 500;
          color: var(--primary);
          background: var(--primary-light);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
        }

        .assigned-by-name {
          font-weight: 500;
          color: var(--secondary);
        }

        .card-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .decrypt-warning {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          background: var(--warning-light);
          border: 1px solid var(--warning);
          border-radius: 8px;
          color: var(--warning-dark);
          font-size: 0.875rem;
          font-weight: 500;
        }

        .decrypt-intro {
          text-align: center;
          padding: 2rem 1rem;
          background: var(--bg-secondary);
          border: 2px dashed var(--border-secondary);
          border-radius: 8px;
        }

        .decrypt-intro p {
          margin-bottom: 1.5rem;
          color: var(--text-secondary);
          font-size: 0.95rem;
        }

        .decrypt-form {
          background: var(--bg-secondary);
          padding: 1.5rem;
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          margin-top: 1rem;
        }

        .decrypt-form .form-group {
          margin-bottom: 1.5rem;
        }

        .decrypt-form label {
          display: block;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .decrypt-form textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-card);
          color: var(--text-primary);
          font-family: inherit;
          font-size: 0.875rem;
          resize: vertical;
          min-height: 80px;
        }

        .decrypt-form textarea:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px var(--primary-light);
        }

        .decrypt-form textarea:disabled {
          background: var(--bg-secondary);
          color: var(--text-muted);
          cursor: not-allowed;
        }

        .decrypt-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        .decrypt-success {
          background: var(--success-light);
          border: 1px solid var(--success);
          border-radius: 8px;
          padding: 1.5rem;
          margin-top: 1rem;
        }

        .decrypt-success .decrypt-warning {
          margin-top: 0;
          margin-bottom: 1rem;
          background: var(--danger-light);
          border-color: var(--danger);
          color: var(--danger-dark);
        }

        .sale-category {
          background: var(--primary-light);
          color: var(--primary);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .sale-state {
          background: var(--secondary-light);
          color: var(--secondary);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .approval-user {
          color: var(--success);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .approval-date {
          color: var(--success);
          font-weight: 500;
          font-size: 0.875rem;
        }

        .rejection-user {
          color: var(--danger);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .rejection-date {
          color: var(--danger);
          font-weight: 500;
          font-size: 0.875rem;
        }

        .rejection-reason {
          color: var(--danger);
          font-weight: 500;
          padding: 0.5rem 0.75rem;
          background: var(--danger-light);
          border: 1px solid var(--danger);
          border-radius: 4px;
          border-left: 4px solid var(--danger);
          font-style: italic;
          line-height: 1.4;
        }

        .workflow-timeline {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .workflow-step {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid transparent;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
        }

        .workflow-step.approval-step {
          border-left-color: var(--success);
          background: var(--success-light);
        }

        .workflow-step.rejection-step {
          border-left-color: var(--danger);
          background: var(--danger-light);
        }

        .workflow-step.void-step {
          border-left-color: var(--danger);
          background: var(--danger-light);
        }

        .workflow-step.refund-step {
          border-left-color: var(--primary);
          background: var(--primary-light);
        }

        .workflow-step.transfer-step {
          border-left-color: var(--secondary);
          background: var(--secondary-light);
        }

        .step-icon {
          font-size: 1.25rem;
          width: 2rem;
          height: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: var(--bg-card);
          border: 2px solid var(--border-primary);
          flex-shrink: 0;
          margin-top: 0.125rem;
        }

        .approval-step .step-icon {
          border-color: var(--success);
          background: var(--success-light);
        }

        .rejection-step .step-icon {
          border-color: var(--danger);
          background: var(--danger-light);
        }

        .void-step .step-icon {
          border-color: var(--danger);
          background: var(--danger-light);
        }

        .refund-step .step-icon {
          border-color: var(--primary);
          background: var(--primary-light);
        }

        .transfer-step .step-icon {
          border-color: var(--secondary);
          background: var(--secondary-light);
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .step-details {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .step-user {
          font-weight: 500;
          color: var(--text-primary);
          font-size: 0.9rem;
        }

        .step-date {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .step-reason {
          margin-top: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--bg-card);
          border: 1px solid var(--border-secondary);
          border-radius: 4px;
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .step-note {
          margin-top: 0.5rem;
          padding: 0.5rem;
          background: var(--primary-light);
          border: 1px solid var(--primary);
          border-radius: 4px;
          font-size: 0.875rem;
          color: var(--primary);
          font-style: italic;
        }

        .void-refund-actions {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--border-primary);
        }

        .void-refund-info {
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: var(--primary-light);
          border: 1px solid var(--primary);
          border-radius: 4px;
        }

        .void-refund-info strong {
          display: block;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .void-refund-info small {
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .void-refund-actions button {
          margin-right: 1rem;
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .void-refund-actions .btn-warning {
          background: var(--warning);
          color: white;
          border: 1px solid var(--warning);
        }

        .void-refund-actions .btn-warning:hover {
          background: var(--warning-dark);
          border-color: var(--warning-dark);
        }

        .void-refund-actions .btn-danger {
          background: var(--danger);
          color: white;
          border: 1px solid var(--danger);
        }

        .void-refund-actions .btn-danger:hover {
          background: var(--danger-dark);
          border-color: var(--danger-dark);
        }

        @media (max-width: 768px) {
          .details-panel-overlay {
            padding: 0.5rem;
          }

          .details-content {
            padding: 1rem;
            background: var(--bg-primary);
          }

          .details-header {
            padding: 1rem 1.5rem;
          }

          .details-section {
            padding: 1rem;
            margin-bottom: 1.5rem;
          }

          .details-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
};